/* ==========================================================================
   SMT Story System  —  smt-dw-header.js  (v2.1.0)
   Vanilla JS for the signal line and the system bar:
     - announcement rotation (prev / next, pause on hover, no auto for reduced motion)
     - desktop panels: hover intent, disclose buttons, keyboard, click-outside
     - phone menu: dialog with focus trap, scroll lock, ESC, scrim
     - search dialog with live results from the Predictive Search API
     - tuck the bar away while scrolling down on phones, bring it back on scroll up
     - --smt-hdr-height on :root for anything that needs to sit under the bar
     - a menu too wide for the bar drops the search label first, then goes to the phone layout
     - "/" opens search on a keyboard (never while typing in a field)
   Loaded with `defer` by both sections; guarded so it initialises once.
   ========================================================================== */
(function () {
  'use strict';
  if (window.SMTDWHeader) return;
  var API = (window.SMTDWHeader = { version: '2.0.0' });

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var phone = window.matchMedia('(max-width: 1039px)');
  var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(amount, fmt) {
    var cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents)) return '';
    if (window.SMTDW && typeof window.SMTDW.money === 'function') return window.SMTDW.money(cents, fmt);
    return '$' + (cents / 100).toFixed(2);
  }
  function trap(container, e) {
    if (e.key !== 'Tab') return;
    var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- announcement ---------------------------------------------- */
  function initAnn(el) {
    if (el.dataset.dwReady) return;
    el.dataset.dwReady = '1';
    var items = $$('[data-dw-ann-item]', el);
    if (items.length < 2) return;
    var i = Math.max(0, items.findIndex(function (it) { return it.classList.contains('is-on'); }));
    var speed = Number(el.dataset.speed || 5000);
    var auto = el.dataset.rotate !== 'off' && !reduced.matches;
    var timer = null, paused = false;
    function show(n) {
      i = (n + items.length) % items.length;
      items.forEach(function (it, k) { it.classList.toggle('is-on', k === i); });
    }
    function tick() { if (!paused && !document.hidden) show(i + 1); }
    function start() { if (auto && !timer) timer = setInterval(tick, speed); }
    function stop() { clearInterval(timer); timer = null; }
    var prev = $('[data-dw-ann-prev]', el), next = $('[data-dw-ann-next]', el);
    if (prev) prev.addEventListener('click', function () { show(i - 1); stop(); start(); });
    if (next) next.addEventListener('click', function () { show(i + 1); stop(); start(); });
    el.addEventListener('mouseenter', function () { paused = true; });
    el.addEventListener('mouseleave', function () { paused = false; });
    el.addEventListener('focusin', function () { paused = true; });
    el.addEventListener('focusout', function () { paused = false; });
    start();
  }

  /* ---------- header ------------------------------------------------------ */
  function initHeader(hdr) {
    if (hdr.dataset.dwReady) return;
    hdr.dataset.dwReady = '1';
    var bar = $('.dw-hdr__bar', hdr);
    var menu = $('[data-dw-menu]', hdr);
    var scrim = $('.dw-hdr__scrim', hdr);
    var search = $('[data-dw-search]', hdr);
    var bag = $('[data-dw-open-cart]', hdr);
    var countEl = $('[data-dw-cart-count]', hdr);
    var moneyFmt = hdr.dataset.dwMoney || '${{amount}}';
    var suggestUrl = (hdr.dataset.dwSearchUrl || '/search/suggest') + '.json';
    var lastFocus = null;

    /* height for anything that sits under the bar (docks, sticky rails) */
    function publishHeight() {
      if (!bar) return;
      document.documentElement.style.setProperty('--smt-hdr-height', bar.offsetHeight + 'px');
    }
    publishHeight();
    if ('ResizeObserver' in window && bar) new ResizeObserver(publishHeight).observe(bar);

    /* if the menu needs more room than the bar gives it: first the search pill drops its
       label (is-snug), and only if that is not enough does the bar fall back to the phone
       layout (is-tight). The width is measured from the items themselves, since a centred
       row that overflows only reports half of the overflow in scrollWidth. */
    var nav = $('.dw-hdr__nav', hdr), list = $('[data-dw-list]', hdr), fitTimer = null;
    function need() {
      var a = list.firstElementChild, b = list.lastElementChild;
      return a && b ? b.getBoundingClientRect().right - a.getBoundingClientRect().left : 0;
    }
    function over() { var room = nav.clientWidth; return room > 0 && need() > room + 1; }
    function fit() {
      if (!nav || !list) return;
      hdr.classList.remove('is-tight', 'is-snug');
      if (phone.matches) return;
      if (over()) {
        hdr.classList.add('is-snug');
        if (over()) hdr.classList.add('is-tight');
      }
    }
    function fitSoon() { clearTimeout(fitTimer); fitTimer = setTimeout(fit, 80); }
    fit();
    window.addEventListener('resize', fitSoon, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);

    /* ----- desktop panels ----- */
    var items = $$('[data-dw-item]', hdr);
    var openItem = null, hoverTimer = null;
    function setPanel(item, on) {
      var panel = $('[data-dw-panel]', item);
      var btn = $('[data-dw-disclose]', item);
      if (!panel) return;
      if (on) {
        if (openItem && openItem !== item) setPanel(openItem, false);
        panel.hidden = false;
        requestAnimationFrame(function () { panel.classList.add('is-open'); });
        item.classList.add('is-open');
        if (btn) btn.setAttribute('aria-expanded', 'true');
        openItem = item;
      } else {
        panel.classList.remove('is-open');
        item.classList.remove('is-open');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        var done = function () { if (!item.classList.contains('is-open')) panel.hidden = true; };
        if (reduced.matches) done(); else setTimeout(done, 200);
        if (openItem === item) openItem = null;
      }
    }
    function closePanels() { if (openItem) setPanel(openItem, false); }
    items.forEach(function (item) {
      var panel = $('[data-dw-panel]', item);
      if (!panel) return;
      var btn = $('[data-dw-disclose]', item);
      if (btn) btn.addEventListener('click', function () { setPanel(item, !item.classList.contains('is-open')); });
      item.addEventListener('mouseenter', function () {
        if (!hoverable.matches || phone.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () { setPanel(item, true); }, 70);
      });
      item.addEventListener('mouseleave', function () {
        if (!hoverable.matches || phone.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () { setPanel(item, false); }, 160);
      });
      item.addEventListener('focusout', function (e) {
        if (!e.relatedTarget || !item.contains(e.relatedTarget)) setPanel(item, false);
      });
    });
    document.addEventListener('click', function (e) {
      if (openItem && !openItem.contains(e.target)) closePanels();
    });

    /* ----- phone menu ----- */
    function menuOpen(trigger) {
      if (!menu) return;
      lastFocus = trigger || document.activeElement;
      closePanels();
      if (scrim) { scrim.hidden = false; requestAnimationFrame(function () { scrim.classList.add('is-open'); }); }
      menu.hidden = false;
      requestAnimationFrame(function () { menu.classList.add('is-open'); });
      document.documentElement.classList.add('dw-hdr-lock');
      $$('[data-dw-menu-open]', hdr).forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      hdr.classList.remove('is-tucked');
      var target = $('[data-dw-menu-close]', menu) || menu;
      setTimeout(function () { target.focus({ preventScroll: true }); }, 60);
      document.addEventListener('keydown', menuKeys);
      hdr.dispatchEvent(new CustomEvent('smt:menu:open', { bubbles: true }));
    }
    function menuClose() {
      if (!menu || menu.hidden) return;
      menu.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      document.documentElement.classList.remove('dw-hdr-lock');
      $$('[data-dw-menu-open]', hdr).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      document.removeEventListener('keydown', menuKeys);
      var done = function () { if (!menu.classList.contains('is-open')) { menu.hidden = true; if (scrim) scrim.hidden = true; } };
      if (reduced.matches) done(); else setTimeout(done, 360);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
      hdr.dispatchEvent(new CustomEvent('smt:menu:close', { bubbles: true }));
    }
    function menuKeys(e) {
      if (e.key === 'Escape') { e.preventDefault(); menuClose(); return; }
      if (menu) trap(menu, e);
    }
    $$('[data-dw-menu-open]', hdr).forEach(function (b) { b.addEventListener('click', function () { menuOpen(b); }); });
    $$('[data-dw-menu-close]', hdr).forEach(function (b) { b.addEventListener('click', menuClose); });
    if (menu) menu.addEventListener('click', function (e) { if (e.target.closest('a[href]')) menuClose(); });
    phone.addEventListener('change', function (e) { if (!e.matches) menuClose(); });

    /* ----- search ----- */
    var input = search ? $('[data-dw-search-input]', search) : null;
    var results = search ? $('[data-dw-search-results]', search) : null;
    var quick = search ? $('[data-dw-search-quick]', search) : null;
    var ctrl = null, debounce = null, lastQ = '';
    function searchOpen(trigger) {
      if (!search) return;
      lastFocus = trigger || document.activeElement;
      closePanels();
      if (menu && !menu.hidden) menuClose();
      search.hidden = false;
      requestAnimationFrame(function () { search.classList.add('is-open'); });
      document.documentElement.classList.add('dw-hdr-lock');
      $$('[data-dw-search-open]', hdr).forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      hdr.classList.remove('is-tucked');
      setTimeout(function () { if (input) input.focus({ preventScroll: true }); }, 40);
      document.addEventListener('keydown', searchKeys);
    }
    function searchClose() {
      if (!search || search.hidden) return;
      search.classList.remove('is-open');
      document.documentElement.classList.remove('dw-hdr-lock');
      $$('[data-dw-search-open]', hdr).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      document.removeEventListener('keydown', searchKeys);
      if (ctrl) { ctrl.abort(); ctrl = null; }
      var done = function () { if (!search.classList.contains('is-open')) search.hidden = true; };
      if (reduced.matches) done(); else setTimeout(done, 220);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    }
    function searchKeys(e) {
      if (e.key === 'Escape') { e.preventDefault(); searchClose(); return; }
      if (search) trap(search, e);
    }
    function hitProduct(p) {
      var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
      var src = img ? img.replace(/(\.[a-z]+)(\?|$)/i, '_120x150_crop_center$1$2') : '';
      var parts = String(p.title || '').split(' — ');
      var name = parts[0];
      var sub = esc(parts.length > 1 ? parts.slice(1).join(' — ') : (p.product_type || ''));
      var price = money(p.price, moneyFmt);
      var cmp = p.compare_at_price_min && parseFloat(p.compare_at_price_min) > parseFloat(p.price) ? '<s>' + esc(money(p.compare_at_price_min, moneyFmt)) + '</s>' : '';
      return '<a class="dw-hdr__hit" href="' + esc(p.url) + '">' +
        '<span class="dw-hdr__hit-media">' + (src ? '<img src="' + esc(src) + '" width="48" height="60" alt="" loading="lazy">' : '') + '</span>' +
        '<span><span class="dw-hdr__hit-title">' + esc(name) + '</span>' + (sub ? '<span class="dw-hdr__hit-sub">' + sub + '</span>' : '') + '</span>' +
        '<span class="dw-hdr__hit-price">' + cmp + esc(price) + '</span></a>';
    }
    function hitFlat(item) {
      return '<a class="dw-hdr__hit dw-hdr__hit--flat" href="' + esc(item.url) + '"><span class="dw-hdr__hit-title">' + esc(item.title) + '</span>' +
        '<svg class="dw-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>';
    }
    function render(q, data) {
      if (!results) return;
      var r = (data && data.resources && data.resources.results) || {};
      var products = r.products || [], collections = r.collections || [], pages = r.pages || [];
      var html = '';
      if (products.length) html += '<p class="dw-hdr__search-label">Pieces</p>' + products.map(hitProduct).join('');
      if (collections.length) html += '<p class="dw-hdr__search-label">Collections</p>' + collections.map(hitFlat).join('');
      if (pages.length) html += '<p class="dw-hdr__search-label">Pages</p>' + pages.map(hitFlat).join('');
      if (!html) html = '<p class="dw-hdr__search-empty">Nothing matches “' + esc(q) + '” yet. Try a shorter word, or browse below.</p>';
      else html += '<a class="dw-hdr__search-all" href="/search?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last">See every result for “' + esc(q) + '”' +
        '<svg class="dw-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>';
      results.innerHTML = html;
      if (quick) quick.hidden = products.length > 0;
    }
    function suggest() {
      if (!input || !results) return;
      var q = input.value.trim();
      if (q === lastQ) return;
      lastQ = q;
      if (ctrl) { ctrl.abort(); ctrl = null; }
      if (q.length < 2) { results.innerHTML = ''; if (quick) quick.hidden = false; return; }
      ctrl = 'AbortController' in window ? new AbortController() : null;
      var url = suggestUrl + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page&resources[limit]=6&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=hide&resources[options][prefix]=last' +
        '&resources[options][fields]=title,product_type,variants.title,tag,vendor';
      fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined })
        .then(function (res) { return res.ok ? res.json() : Promise.reject(res); })
        .then(function (data) { if (input.value.trim() === q) render(q, data); })
        .catch(function (err) { if (err && err.name === 'AbortError') return; results.innerHTML = ''; if (quick) quick.hidden = false; });
    }
    if (input) input.addEventListener('input', function () { clearTimeout(debounce); debounce = setTimeout(suggest, 180); });
    $$('[data-dw-search-open]', hdr).forEach(function (b) { b.addEventListener('click', function () { searchOpen(b); }); });
    $$('[data-dw-search-close]', hdr).forEach(function (b) { b.addEventListener('click', searchClose); });
    if (search) search.addEventListener('click', function (e) { if (e.target === search) searchClose(); });
    /* the phone menu's search field opens the dialog on desktop-sized screens only if asked; on phones it submits normally */

    /* ----- bag count: label + bump ----- */
    function syncBagLabel() {
      if (!bag || !countEl) return;
      var n = Number(countEl.textContent || 0) || 0;
      bag.setAttribute('aria-label', 'Bag, ' + n + (n === 1 ? ' item' : ' items'));
      countEl.classList.remove('is-bump');
      void countEl.offsetWidth;
      if (n > 0) countEl.classList.add('is-bump');
    }
    document.addEventListener('smt:cart:updated', function () { setTimeout(syncBagLabel, 30); });
    if (typeof window.subscribe === 'function') {
      try { window.subscribe('cart-update', function () { setTimeout(syncBagLabel, 30); }); } catch (e) {}
    }

    /* ----- tuck away on scroll (phones) ----- */
    if (hdr.hasAttribute('data-dw-hide-scroll')) {
      var lastY = window.scrollY, ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var y = window.scrollY;
          var dy = y - lastY;
          lastY = y;
          if (!phone.matches) { hdr.classList.remove('is-tucked'); return; }
          if (document.documentElement.classList.contains('dw-hdr-lock') || document.documentElement.classList.contains('dw-cart-lock')) return;
          if (y < 24 || dy < -4) hdr.classList.remove('is-tucked');
          else if (y > 160 && dy > 6) hdr.classList.add('is-tucked');
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* ----- global escape for panels, "/" for search ----- */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closePanels(); return; }
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || !search || !search.hidden) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (menu && !menu.hidden) return;
      e.preventDefault();
      searchOpen($('[data-dw-search-open]', hdr));
    });

    API.openMenu = menuOpen;
    API.closeMenu = menuClose;
    API.openSearch = searchOpen;
    API.closeSearch = searchClose;
  }

  function boot(root) {
    $$('[data-dw-ann]', root).forEach(initAnn);
    $$('[data-dw-hdr]', root).forEach(initHeader);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  API.boot = boot;
})();
